import { Box, type SxProps, type Theme, Typography } from "@mui/material";
import { type FC, type ReactNode, useId } from "react";
import { FONT_SIZE, INK, LAYOUT_OFFSET, MOTION, NEUTRAL, SHADOW, STATUS_BADGE, SURFACE } from "../../../theme/tokens";
import { AccentIconTile } from "./AccentIconTile";

export type SettingsSectionLayout = "grid" | "stack";
export type SettingsSectionHeaderPlacement = "top" | "side";

type Props = {
	title: string;
	/** Brand-tinted identity tile: rendered above the title in `side` placement, left of the title in every header-based layout (`top`/stack/`headerOnly`). */
	icon?: ReactNode;
	description?: ReactNode;
	/** Top: right of the header. Side: under the description. */
	action?: ReactNode;
	children?: ReactNode;
	/** `"grid"` (default) auto-fits children into a responsive grid; `"stack"` is a vertical column. */
	layout?: SettingsSectionLayout;
	/** `"top"` (default) stacks body under header in a card; `"side"` is borderless, header left + controls right (pair with `divider` to stack). */
	headerPlacement?: SettingsSectionHeaderPlacement;
	minColumnWidth?: number;
	columns?: number;
	/** Max width of the `side` controls block (px); `"none"` lets wide content fill the width. */
	contentMaxWidth?: number | "none";
	sideHeaderWidth?: number;
	/** Render only the header without the body container. */
	headerOnly?: boolean;
	/** Top: hairline between header and body. Side: hairline below the section. */
	divider?: boolean;
	/**
	 * Suppress the built-in mount fade/lift entrance. Use when a caller layers its OWN one-shot
	 * entrance on this section (e.g. a "spotlight" highlight on a freshly-added item) so the two
	 * animations don't play over each other. Off by default — existing callers keep the fade.
	 */
	disableEntrance?: boolean;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

const DEFAULT_MIN_COLUMN_WIDTH = 240;
const DEFAULT_CONTENT_MAX_WIDTH = 760;
const DEFAULT_SIDE_HEADER_WIDTH = 280;

// Mount entrance: fade + lift, transform/opacity only; reduced-motion drops it.
const ENTRANCE_SX: SxProps<Theme> = {
	"@keyframes settingsSectionEnter": {
		from: { opacity: 0, transform: "translateY(8px)" },
		to: { opacity: 1, transform: "translateY(0)" },
	},
	animation: `settingsSectionEnter ${MOTION.DURATION.ENTRANCE_SM}ms ${MOTION.EASE_OUT}`,
	"@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

const TITLE_SX: SxProps<Theme> = { fontWeight: 600, fontSize: FONT_SIZE.TITLE_MD, lineHeight: 1.2, color: STATUS_BADGE.BRAND_FALLBACK };
const SIDE_TITLE_SX: SxProps<Theme> = {
	fontWeight: 600,
	fontSize: FONT_SIZE.TITLE_LG,
	lineHeight: 1.15,
	letterSpacing: "-0.01em",
	color: STATUS_BADGE.BRAND_FALLBACK,
};
const DESCRIPTION_SX: SxProps<Theme> = { fontSize: FONT_SIZE.SECONDARY, lineHeight: 1.4, color: INK.MUTED };

/**
 * Layout primitive for settings/config forms. Placements: `top` (header above a card grid),
 * `side` (header left, width-capped controls right), `headerOnly` (bare title above other content).
 */
export const SettingsSection: FC<Props> = ({
	title,
	icon,
	description,
	action,
	children,
	layout = "grid",
	headerPlacement = "top",
	minColumnWidth = DEFAULT_MIN_COLUMN_WIDTH,
	columns,
	contentMaxWidth = DEFAULT_CONTENT_MAX_WIDTH,
	sideHeaderWidth = DEFAULT_SIDE_HEADER_WIDTH,
	headerOnly = false,
	divider = false,
	disableEntrance = false,
	dataTestId,
	sx,
}) => {
	const headingId = useId();
	// The mount entrance, unless the caller is layering its own one-shot animation on this section.
	const entranceSx: SxProps<Theme> | null = disableEntrance ? null : ENTRANCE_SX;

	// Uses MUI breakpoint keys, not a raw `@media` key (emotion rejects that inside a property-value object).
	const gridTemplateColumns: SxProps<Theme> =
		columns != null
			? {
					xs: "1fr",
					sm: `repeat(auto-fill, minmax(${minColumnWidth}px, 1fr))`,
					md: `repeat(${columns}, 1fr)`,
				}
			: {
					xs: "1fr",
					sm: `repeat(auto-fill, minmax(${minColumnWidth}px, 1fr))`,
				};

	const body = (
		<Box
			sx={
				layout === "grid"
					? // `minWidth: 0` on each grid item overrides the default `min-width: auto`, so a wide child
						// (e.g. a many-chip multi-select) shrinks/truncates inside its track instead of overflowing
						// onto neighboring cells.
						{ display: "grid", gridTemplateColumns, gap: 2, alignItems: "start", "& > *": { minWidth: 0 } }
					: { display: "flex", flexDirection: "column", gap: 2 }
			}
		>
			{children}
		</Box>
	);

	if (headerPlacement === "side") {
		return (
			<Box
				component="section"
				aria-labelledby={headingId}
				data-testid={dataTestId}
				sx={[
					entranceSx,
					{
						display: "flex",
						flexDirection: { xs: "column", md: "row" },
						gap: { xs: 2, md: 6 },
						alignItems: "flex-start",
						py: { xs: 3, md: 4 },
						...(divider && { borderBottom: `1px solid ${NEUTRAL[200]}` }),
					},
					...(Array.isArray(sx) ? sx : [sx]),
				]}
			>
				<Box
					sx={{
						width: { xs: "100%", md: sideHeaderWidth },
						flexShrink: 0,
						display: "flex",
						flexDirection: "column",
						gap: 1,
						// Sticky on desktop so the header stays in view while scanning a tall controls panel.
						position: { md: "sticky" },
						top: { md: LAYOUT_OFFSET.SETTINGS_STICKY_TOP },
					}}
				>
					{icon && <AccentIconTile size="sm">{icon}</AccentIconTile>}
					<Typography id={headingId} component="h2" sx={SIDE_TITLE_SX}>
						{title}
					</Typography>
					{description && <Typography sx={DESCRIPTION_SX}>{description}</Typography>}
					{action && <Box sx={{ mt: 0.5 }}>{action}</Box>}
				</Box>
				<Box
					sx={(theme) => ({
						flex: 1,
						minWidth: 0,
						width: "100%",
						maxWidth: contentMaxWidth,
						backgroundColor: SURFACE,
						border: `1px solid ${NEUTRAL[200]}`,
						borderRadius: `${Number(theme.shape.borderRadius) * 1.5}px`,
						boxShadow: SHADOW.PANEL_SOFT,
						p: { xs: 2, md: 3 },
						transition: `box-shadow ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}`,
						"@media (hover: hover) and (pointer: fine)": {
							"&:hover": { boxShadow: SHADOW.PANEL_SOFT_HOVER },
						},
						"@media (prefers-reduced-motion: reduce)": { transition: "none" },
					})}
				>
					{body}
				</Box>
			</Box>
		);
	}

	const header = (
		<Box
			sx={{
				display: "flex",
				flexDirection: { xs: "column", sm: "row" },
				alignItems: { xs: "flex-start", sm: "center" },
				justifyContent: "space-between",
				gap: 1,
			}}
		>
			<Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 1.5, minWidth: 0 }}>
				{icon && <AccentIconTile size="sm">{icon}</AccentIconTile>}
				<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
					<Typography id={headingId} component="h2" sx={TITLE_SX}>
						{title}
					</Typography>
					{description && <Typography sx={DESCRIPTION_SX}>{description}</Typography>}
				</Box>
			</Box>
			{action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
		</Box>
	);

	if (headerOnly) {
		return (
			<Box component="section" aria-labelledby={headingId} data-testid={dataTestId} sx={[entranceSx, ...(Array.isArray(sx) ? sx : [sx])]}>
				{header}
			</Box>
		);
	}

	return (
		<Box
			component="section"
			aria-labelledby={headingId}
			data-testid={dataTestId}
			sx={[
				entranceSx,
				(theme) => ({
					display: "flex",
					flexDirection: "column",
					gap: 2,
					backgroundColor: SURFACE,
					border: `1px solid ${NEUTRAL[200]}`,
					borderRadius: `${Number(theme.shape.borderRadius) * 3}px`,
					p: { xs: 2, md: 3 },
				}),
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		>
			{header}
			{divider && <Box sx={{ height: "1px", backgroundColor: NEUTRAL[200] }} />}
			{body}
		</Box>
	);
};
